import { Component, Host, h, Prop, Watch, State, Element, JSX } from '@stencil/core';
import debounce from 'debounce';
import { CmsImage } from '../../types/global';
import { isMagnoliaEditingMode } from '../../helper';
import { replaceCheckmarkIcons } from '../../global/utility/utils';

interface CmsImageInfobox extends CmsImage {
  afterText: boolean;
}

interface UiCmsTextInfoboxBody {
  backgroundStyle: 'primary' | 'primary-100' | 'border-neutral-400' | 'white';
  position: 'float-right' | 'float-left' | 'no-float';
  text: string;
  headline?: string;
  level?: 2 | 3 | 4;
  leadtext?: string;
  image: CmsImageInfobox;
}

@Component({
  tag: 'ui-cms-text-infobox',
  styleUrls: {
    default: 'ui-cms-text-infobox.scss',
  },
})
export class UiCmsTextInfobox {
  @Element() el: HTMLElement;

  @Prop({ mutable: true }) body: UiCmsTextInfoboxBody = {} as any;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Prop({ reflect: true, mutable: true }) invalid = false;

  @Prop({ attribute: 'i18n' }) utilityI18n: string;

  @Prop() inverted: boolean;

  @Prop({ mutable: true }) i18n = {
    warning: 'WARNUNG - "Text mit Infobox" ist nur in Layout "2/3 Seitenbreite (zentriert)" erlaubt',
  };

  @State() error = this.i18n.warning;

  @State() currentElementWidth: number;

  private container: HTMLUiCmsContainerElement;

  private resizeObserver: ResizeObserver;

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  @Watch('utilityI18n')
  utilityI18nChangeHandler(newValue: string) {
    if (newValue) this.i18n = { ...this.i18n, ...JSON.parse(newValue) };
  }

  componentDidLoad() {
    replaceCheckmarkIcons(this.el);
    this.addResizeObserver();
    this.addTriangleClass();
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  checkForLayoutValidity() {
    const parentGridElement = this.el.closest('ui-cms-grid-element');
    if (parentGridElement) {
      this.invalid = parentGridElement.columns !== 8 && parentGridElement.centered;
      this.error = this.invalid ? this.i18n.warning : '';
    }
  }

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
    this.utilityI18nChangeHandler(this.utilityI18n);
    if (isMagnoliaEditingMode()) this.checkForLayoutValidity();
  }

  private addResizeObserver() {
    if (!this.el) return;
    this.resizeObserver = new ResizeObserver(
      debounce(() => {
        this.currentElementWidth = this.el.offsetWidth;
        this.addTriangleClass();
      }, 200)
    );
    this.resizeObserver.observe(this.el);
  }

  private addTriangleClass() {
    let newClass: string;

    if (!this.container || !this.body.position) return;

    switch (this.body.position) {
      case 'float-right':
        newClass = 'sd-container--triangle-left';
        break;
      case 'float-left':
        newClass = 'sd-container--triangle-right';
        break;
      case 'no-float':
        newClass = 'sd-container--triangle-top';
        break;
      default:
        newClass = '';
    }
    if (window.innerWidth < 600) {
      newClass = 'sd-container--triangle-top';
    }

    if (this.body.backgroundStyle === 'border-neutral-400') {
      newClass = `${newClass}-border`;
    }
    this.container.classList.remove(
      'sd-container--triangle-left',
      'sd-container--triangle-right',
      'sd-container--triangle-top',
      'sd-container--triangle-right-border',
      'sd-container--triangle-left-border',
      'sd-container--triangle-top-border'
    );

    if (newClass) this.container.classList.add(newClass);
  }

  private renderImage(): JSX.Element {
    if (!this.body.image || !this.body.image.src) return null;
    const { objectPosition, alt, src, mobileSrc, copyright, caption } = this.body.image;

    return (
      <div class="text-infobox__text-Wrapper__container--img">
        <figure>
          <div class="sd-copyright" style={{ '--copyright': copyright ? `'© ${copyright}'` : '' }}>
            <ui-cms-image
              size={2400}
              src={this.currentElementWidth < 480 && mobileSrc ? mobileSrc : src}
              alt={alt}
              loading="eager"
              objectPosition={objectPosition}
            ></ui-cms-image>
          </div>
          {caption && (
            <figcaption>
              <p
                class={{
                  'sd-paragraph sd-paragraph--size-sm text-infobox__text-Wrapper__container--img-caption': true,
                  'sd-paragraph--inverted': this.inverted,
                }}
                innerHTML={caption}
              ></p>
            </figcaption>
          )}
        </figure>
      </div>
    );
  }

  render() {
    return (
      <Host>
        <sd-notification open={this.invalid} variant="error" toast-stack="top-right" duration="Infinity">
          <div class="flex items-center h-6">{this.i18n.warning}</div>
        </sd-notification>
        <div
          class={{
            'text-infobox': true,
            [`text-infobox--${this.body.position}`]: !!this.body.position,
          }}
        >
          <ui-cms-container
            ref={(el) => {
              this.container = el;
            }}
            body={{
              backgroundStyle: this.body.backgroundStyle,
            }}
          >
            <slot></slot>
          </ui-cms-container>
          <div class="text-infobox__text-wrapper">
            {this.body.headline && (
              <ui-cms-headline
                body={{ headline: this.body.headline, leadtext: this.body.leadtext, headingLevel: this.body.level }}
              ></ui-cms-headline>
            )}
            <div
              class={{
                'text-infobox__text-Wrapper__container': true,
                'text-infobox__text-Wrapper__container--reverse': this.body.image.afterText,
              }}
            >
              {this.renderImage()}
              <div
                class={{
                  'text-infobox--richtext sd-prose': true,
                  'sd-prose--inverted': this.inverted,
                }}
                innerHTML={this.body.text}
              ></div>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
